import {
  LayoutDashboard, Target, TrendingUp, Users, UserCircle,
  CalendarDays, BarChart3, Zap, Settings, Database, BookOpen, LineChart, ShieldCheck, Building2,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, type Permission } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/BrandLogo";
import { useQuery } from "@tanstack/react-query";
import { settingsApi } from "@/lib/api";

const crmItems = [
  { title: "Home", url: "/", icon: LayoutDashboard, permission: "view_dashboard" as Permission },
  { title: "Leads", url: "/leads", icon: Target, permission: "view_leads" as Permission },
  { title: "Deals", url: "/deals", icon: TrendingUp, permission: "view_deals" as Permission },
  { title: "Customers", url: "/customers", icon: Users, permission: "view_customers" as Permission },
  { title: "Contacts", url: "/contacts", icon: UserCircle, permission: "view_contacts" as Permission },
  { title: "Activities", url: "/activities", icon: CalendarDays, permission: "view_activities" as Permission },
];

const analyticsItems = [
  { title: "Reports", url: "/reports", icon: BarChart3, permission: "view_reports" as Permission },
  { title: "Forecast", url: "/forecast", icon: LineChart, permission: "view_reports" as Permission },
  { title: "Playbooks", url: "/playbooks", icon: BookOpen, permission: "view_deals" as Permission },
];

const systemItems = [
  { title: "Master", url: "/master", icon: Database, permission: "view_settings" as Permission },
  { title: "Automation", url: "/automation", icon: Zap, permission: "view_automation" as Permission },
  { title: "Access Control", url: "/access-control", icon: ShieldCheck, permission: "manage_users" as Permission },
  { title: "Settings", url: "/settings", icon: Settings, permission: "view_settings" as Permission },
];

interface AppSidebarProps {
  collapsed?: boolean;
}

export function AppSidebar({ collapsed = false }: AppSidebarProps) {
  const location = useLocation();
  const { hasPermission } = useAuth();

  // Fetch company name to display in the sidebar header
  const { data: companyData } = useQuery({
    queryKey: ["sidebar-company"],
    queryFn: () => settingsApi.getCompany(),
    staleTime: 10 * 60_000,
  });
  const companyName = companyData?.companyName ?? "";

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const renderItems = (items: typeof crmItems) => {
    const visible = items.filter((item) => hasPermission(item.permission));

    if (collapsed) {
      return (
        <div className="flex flex-col gap-0.5">
          {visible.map((item) => {
            const active = isActive(item.url);
            return (
              <Tooltip key={item.title} delayDuration={100}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.url}
                    end={item.url === "/"}
                    className={cn(
                      "flex items-center justify-center h-8 w-8 rounded-md mx-auto transition-all duration-150",
                      active
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                    activeClassName=""
                  >
                    <item.icon className={cn("h-4 w-4 shrink-0", active && "text-primary")} />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs font-medium">
                  {item.title}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      );
    }

    return (
      <SidebarMenu className="gap-0.5">
        {visible.map((item) => {
          const active = isActive(item.url);
          return (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild isActive={active} className="h-8">
                <NavLink
                  to={item.url}
                  end={item.url === "/"}
                  className={cn(
                    "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-all duration-150",
                    active
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                  activeClassName=""
                >
                  <item.icon className={cn("h-4 w-4 shrink-0", active && "text-primary")} />
                  <span>{item.title}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    );
  };

  return (
    <TooltipProvider>
      <Sidebar
        collapsible="none"
        className={cn(
          "h-screen sticky top-0 border-r border-sidebar-border bg-sidebar transition-all duration-200",
          collapsed ? "w-[52px]" : "w-64"
        )}
      >
        {/* Brand + company — logo left, full company name right */}
        {collapsed ? (
          <div className="flex h-14 items-center justify-center border-b border-sidebar-border">
            <BrandLogo size="sm" showWordmark={false} />
          </div>
        ) : (
          <div className="border-b border-sidebar-border px-3 py-3">
            <div className="flex items-center gap-3">
              <div className="shrink-0 flex items-center justify-center w-[42%]">
                <BrandLogo size="md" showWordmark={false} className="h-14 w-auto max-w-full" />
              </div>
              <div className="w-px self-stretch bg-sidebar-border shrink-0" />
              <div className="min-w-0 flex-1 flex items-start gap-1.5">
                {companyName ? (
                  <>
                    <Building2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs font-semibold text-foreground leading-snug break-words">
                      {companyName}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">Company</span>
                )}
              </div>
            </div>
          </div>
        )}

        <SidebarContent className={cn("py-3 flex flex-col justify-between", collapsed ? "px-1.5" : "px-2.5")}>
          <div className="space-y-4">
            {/* CRM group */}
            <SidebarGroup className="p-0">
              {!collapsed && (
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2.5 mb-1.5">CRM</p>
              )}
              <SidebarGroupContent>
                {renderItems(crmItems)}
              </SidebarGroupContent>
            </SidebarGroup>

            <Separator className="bg-border/60" />

            {/* Analytics group */}
            <SidebarGroup className="p-0">
              {!collapsed && (
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2.5 mb-1.5">Analytics</p>
              )}
              <SidebarGroupContent>
                {renderItems(analyticsItems)}
              </SidebarGroupContent>
            </SidebarGroup>
          </div>

          {/* System group — pinned to bottom */}
          <div>
            <Separator className="mb-2 bg-border/60" />
            <SidebarGroup className="p-0">
              {!collapsed && (
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2.5 mb-1.5">System</p>
              )}
              <SidebarGroupContent>
                {renderItems(systemItems)}
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        </SidebarContent>
      </Sidebar>
    </TooltipProvider>
  );
}
